import { Link } from 'react-router-dom';
import type { Manga } from '@/mocks/manga';
import { categoryMap } from '@/mocks/manga';

interface MangaCardProps {
  manga: Manga;
  variant?: 'default' | 'compact' | 'featured';
}

function formatViews(views: number): string {
  if (views >= 10000) {
    return (views / 10000).toFixed(1) + '万';
  }
  return views.toString();
}

export default function MangaCard({ manga, variant = 'default' }: MangaCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${manga.id}`}
        className="group flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-background-100 transition-colors duration-200"
      >
        <span className="text-foreground-300 text-xs font-bold w-5 text-center">
          {manga.id}
        </span>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-800 truncate group-hover:text-primary-500 transition-colors duration-200">
            {manga.title}
          </h4>
          <p className="text-xs text-foreground-400 mt-0.5">{categoryMap[manga.category]} · {manga.author}</p>
        </div>
        <span className="text-xs text-accent-500 font-semibold whitespace-nowrap">{manga.rating}</span>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${manga.id}`}
        className="group relative block rounded-lg overflow-hidden bg-background-100 hover:shadow-md transition-all duration-300"
      >
        <div className="aspect-[3/4] overflow-hidden">
          <img
            src={manga.cover}
            alt={`${manga.title} 漫画封面`}
            title={`${manga.title} - 香香漫画免费在线阅读`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3">
          <h3 className="text-white text-sm font-semibold truncate">{manga.title}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-white/70">{categoryMap[manga.category]}</span>
            <span className="text-xs text-accent-400 font-medium">{manga.rating}分</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${manga.id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 hover:bg-background-100 transition-all duration-300"
    >
      <div className="aspect-[3/4] overflow-hidden">
        <img
          src={manga.cover}
          alt={`${manga.title} 漫画封面`}
          title={`${manga.title} - 香香漫画免费在线阅读`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-3">
        <h3 className="font-semibold text-foreground-900 text-sm truncate group-hover:text-primary-500 transition-colors duration-200">
          {manga.title}
        </h3>
        <div className="flex items-center gap-2 mt-1.5">
          <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
            {categoryMap[manga.category]}
          </span>
          {manga.status === '连载中' && (
            <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
              连载中
            </span>
          )}
          {manga.status === '已完结' && (
            <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
              已完结
            </span>
          )}
        </div>
        <div className="flex items-center justify-between mt-2 text-xs text-foreground-400">
          <span>{formatViews(manga.views)} 阅读</span>
          <span className="text-accent-500 font-medium">{manga.rating}</span>
        </div>
      </div>
    </Link>
  );
}